import React, { useState } from 'react';
import { Avatar, Button, Stack, Typography, Box, Container, Paper, Divider, Skeleton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import Link from '@mui/material/Link';
import DateRangePicker, { DateRange } from '@/pages/points/DateRangePicker';
import useGetPointsData from '@/pages/points/hooks/useGetPointsData';
import { smartRound, numberWithCommas } from '@/util';
import DataComponent from '@/shared/DataComponent';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PointsTable from './PointsTable';
import PointsChart from './PointsChart';
import LOGOS from '../../../images/logos';

const ACHIEVEMENT_ICONS = Object.freeze({
  daily_trade: TrendingUpIcon,
  spot_trader: BarChartIcon,
  perp_trader: TaskAltIcon,
  first_referral: PeopleOutlineIcon,
  first_trade: WorkspacePremiumIcon,
  first_algo: SmartToyIcon,
  first_auto: ElectricBoltIcon,
  high_volume_trader: AttachMoneyIcon,
});

function NoPointsActivity() {
  return (
    <Stack
      alignItems='center'
      direction='column'
      justifyContent='center'
      spacing={2}
      sx={{ height: '100%', minHeight: 'inherit' }}
    >
      <img alt='Tread Logo' src={LOGOS.treadRoundedSvg} style={{ height: '64px' }} />
      <Typography variant='h6'>You don’t have any points rewards right now</Typography>
      <Button href='/' size='large' variant='contained'>
        Go to trade
      </Button>
    </Stack>
  );
}

function PointsSection({ title, subtitle, children }) {
  return (
    <Stack direction='column' spacing={4}>
      <Stack direction='column' spacing={1}>
        <Typography variant='h1'>{title}</Typography>
        <Typography variant='subtitle1'>{subtitle}</Typography>
      </Stack>
      {children}
    </Stack>
  );
}

function AchievementCard({ achievement }) {
  const completed = Boolean(achievement.granted_date);
  const Icon = ACHIEVEMENT_ICONS[achievement.name];
  return (
    <Paper
      elevation={0}
      sx={{ boxSizing: 'border-box', height: '200px', width: '100%', p: 4, opacity: completed ? 1 : 0.5 }}
    >
      <Stack direction='column' justifyContent='space-between' spacing={1} sx={{ height: '100%' }}>
        <Stack direction='column' spacing={1}>
          <Stack alignItems='center' direction='row' spacing={2}>
            <Icon color='text.secondary' fontSize='small' />
            <Typography variant='subtitle1'>{achievement.title}</Typography>
          </Stack>
          <Typography variant='body2'>{achievement.subtitle}</Typography>
        </Stack>
        <Stack alignItems='center' direction='row' justifyContent='space-between'>
          <Typography color='primary' variant='subtitle1'>
            +{achievement.award_points} points
          </Typography>
          <Typography variant='small1'>{completed ? 'Complete' : 'Incomplete'}</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function PointsPage() {
  const theme = useTheme();
  const [dateRange, setDateRange] = useState(DateRange.MONTH);
  const [activityPage, setActivityPage] = useState(0);
  const { pointsData, pointsDataLoading } = useGetPointsData(dateRange, activityPage);

  const {
    total_points = 0,
    total_volume = 0,
    points_chart_data = [],
    points_activity = [],
    points_activity_count = 0,
    achievements = {},
  } = pointsData;

  const dailys = Object.values(achievements).filter((a) => a.is_daily);
  const one_times = Object.values(achievements).filter((a) => !a.is_daily);

  return (
    <Container fixed maxWidth='lg' sx={{ py: 8 }}>
      <Stack direction='column' spacing={6}>
        <PointsSection
          subtitle={
            <>
              Accumulate points by trading on tread.fi.
              <Link
                href='https://foul-wavelength-9af.notion.site/Tread-fi-Points-Program-13f60eedf44c80f98812fdb7a3fbb5c0'
                underline='none'
              >
                {' '}
                Learn more
              </Link>
            </>
          }
          title='Points'
        >
          <Paper
            elevation={0}
            sx={{
              boxSizing: 'border-box',
              height: '200px',
              width: '100%',
              border: `1px solid ${theme.palette.grey[600]}`,
              background: theme.palette.background.paper,
            }}
          >
            <DataComponent
              emptyComponent={<NoPointsActivity />}
              isEmpty={points_activity_count === 0}
              isLoading={pointsDataLoading}
              loadingComponent=<Skeleton sx={{ height: '100%', width: '100%' }} variant='rounded' />
            >
              <Grid container sx={{ height: '100%' }}>
                <Grid sx={{ height: '100%', borderRight: '1px solid rgba(255, 255, 255, 0.12);' }} xs={3}>
                  <Stack direction='column' spacing={4} sx={{ p: 4 }}>
                    <Stack direction='column' spacing={2}>
                      <Typography variant='body1'>Total Points</Typography>
                      <Typography variant='h5Strong'>{numberWithCommas(smartRound(total_points, 2))}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction='column' spacing={2}>
                      <Typography variant='body1'>Badges</Typography>
                      <Stack direction='row' spacing={1}>
                        {one_times.map((a) => {
                          const completed = Boolean(a.granted_date);
                          const Icon = ACHIEVEMENT_ICONS[a.name];

                          return (
                            <Tooltip key={a.name} placement='top' title={a.title}>
                              <Avatar
                                sx={{
                                  border: '1px solid',
                                  borderColor: completed ? 'primary.main' : 'text.grey',
                                  bgcolor: theme.palette.background.paper,
                                  width: 30,
                                  height: 30,
                                }}
                              >
                                <Icon fontSize='small' sx={{ color: completed ? 'text.primary' : 'text.grey' }} />
                              </Avatar>
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid sx={{ height: '100%', borderRight: '1px solid rgba(255, 255, 255, 0.12);' }} xs={3}>
                  <Stack direction='column' spacing={2} sx={{ p: 4 }}>
                    <Typography variant='body1'>Total Volume</Typography>
                    <Typography variant='h5Strong'>${numberWithCommas(smartRound(total_volume, 2))}</Typography>
                  </Stack>
                </Grid>
                <Grid sx={{ height: '100%' }} xs={6}>
                  <Stack direction='column' spacing={1} sx={{ p: 4, pb: 0, boxSizing: 'border-box', height: '100%' }}>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography>Points Chart</Typography>
                      <DateRangePicker dateRange={dateRange} onSelectDateRange={setDateRange} />
                    </Stack>
                    <Box style={{ height: '100%' }}>
                      <PointsChart dateRange={dateRange} pointsData={points_chart_data} />
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </DataComponent>
          </Paper>
        </PointsSection>
        <PointsSection subtitle='Earn points by completing daily tasks.' title='Daily Tasks'>
          <DataComponent
            isLoading={pointsDataLoading}
            loadingComponent=<Skeleton sx={{ height: '200px', width: '100%' }} variant='rounded' />
          >
            <Grid container spacing={4}>
              {dailys.map((achievement) => {
                return (
                  <Grid key={achievement.name} xs={12 / dailys.length}>
                    <AchievementCard achievement={achievement} />
                  </Grid>
                );
              })}
            </Grid>
          </DataComponent>
        </PointsSection>
        <PointsSection
          subtitle='Unlock badges and earn points by completing the following tasks.'
          title='One-Time Achievements'
        >
          <DataComponent
            isLoading={pointsDataLoading}
            loadingComponent=<Skeleton sx={{ height: '200px', width: '100%' }} variant='rounded' />
          >
            <Grid container spacing={4}>
              {one_times.map((achievement) => {
                return (
                  <Grid key={achievement.name} xs={12 / one_times.length}>
                    <AchievementCard achievement={achievement} />
                  </Grid>
                );
              })}
            </Grid>
          </DataComponent>
        </PointsSection>
        <PointsSection subtitle='Track your points earned from trading activities.' title='Points History'>
          <Paper elevation={0} sx={{ minHeight: '400px' }}>
            <DataComponent
              emptyComponent={<NoPointsActivity />}
              isEmpty={points_activity_count === 0}
              isLoading={pointsDataLoading}
              loadingComponent=<Skeleton sx={{ height: '100%', width: '100%' }} variant='rounded' />
            >
              <PointsTable
                achievements={achievements}
                activityPage={activityPage}
                pointsActivity={points_activity}
                pointsActivityCount={points_activity_count}
                onPageChange={setActivityPage}
              />
            </DataComponent>
          </Paper>
        </PointsSection>
      </Stack>
    </Container>
  );
}

export default PointsPage;
